import { useCallback } from "react"
import { useMutation } from "urql"

import {
  EditPurchasingCostDocument,
  type EditPurchasingCostFailure,
  type EditPurchasingCostInput,
  type EditPurchasingCostMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

import "../graphql/mutations"

interface UseUpdatePurchasingCostReturn {
  updatePurchasingCost: (input: EditPurchasingCostInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdatePurchasingCost = (): UseUpdatePurchasingCostReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<EditPurchasingCostMutation, { input: EditPurchasingCostInput }>(
    EditPurchasingCostDocument
  )

  const updatePurchasingCost = useCallback(
    async (input: EditPurchasingCostInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.editPurchasingCost
      const hasError = error || output?.__typename === "EditPurchasingCostFailure"

      if (hasError) {
        const message = error?.message ?? (output as EditPurchasingCostFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Purchasing cost updated" })
      return true
    },
    [executeMutation, notify]
  )

  return { updatePurchasingCost, isUpdating: fetching }
}
